import { useState, useEffect } from 'react'
import type {
  SectionGranularConfig,
  AccessLevel,
  RegionFilter,
  ShowFilter,
} from '../types/permissions'
import { AVAILABLE_SECTIONS } from '../types/permissions'
import { getDataAPI } from '../lib/api'

// ─── US / Canada states for the region picker ────────────────────────
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
]
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT']
const ALL_REGIONS = [...US_STATES, ...CA_PROVINCES].sort()

const COUNTRIES = ['US', 'CA', 'UK', 'AU', 'DE', 'FR', 'JP', 'BR', 'MX', 'Other']

// ─── Props ───────────────────────────────────────────────────────────

type Props = {
  /** Which sections are enabled */
  selectedSections: string[]
  onToggleSection: (id: string) => void
  /** Granular config for each enabled section */
  sectionConfig: SectionGranularConfig
  onUpdateConfig: (config: SectionGranularConfig) => void
  /** Role of the team member (support is always view-only) */
  role: 'manager' | 'support'
  /** Optional: only show these section IDs (e.g. ['shows', 'schedule']) */
  visibleSections?: string[]
}

// ─── Component ───────────────────────────────────────────────────────

export default function SectionPermissionConfig({
  selectedSections,
  onToggleSection,
  sectionConfig,
  onUpdateConfig,
  role,
  visibleSections,
}: Props) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null)

  const updateSectionField = (sectionId: string, field: string, value: any) => {
    const current = (sectionConfig as any)[sectionId] || {}
    onUpdateConfig({
      ...sectionConfig,
      [sectionId]: { ...current, [field]: value },
    })
  }

  const updateNestedField = (sectionId: string, parent: string, field: string, value: any) => {
    const current = (sectionConfig as any)[sectionId] || {}
    const parentObj = current[parent] || {}
    onUpdateConfig({
      ...sectionConfig,
      [sectionId]: {
        ...current,
        [parent]: { ...parentObj, [field]: value },
      },
    })
  }

  const getAccessLevel = (sectionId: string): AccessLevel => {
    if (role === 'support') return 'view'
    return ((sectionConfig as any)[sectionId]?.accessLevel as AccessLevel) || 'edit'
  }

  const getFinancialAccess = (sectionId: string): boolean => {
    if (role === 'support') return false
    return (sectionConfig as any)[sectionId]?.financialAccess ?? true
  }

  const getRegionFilter = (sectionId: string): RegionFilter => {
    return (sectionConfig as any)[sectionId]?.regionFilter ?? {
      enabled: false,
      cities: [],
      states: [],
      countries: [],
    }
  }

  const getShowFilter = (sectionId: string): ShowFilter => {
    return (sectionConfig as any)[sectionId]?.showFilter ?? {
      enabled: false,
      mode: 'allowlist',
      showIds: [],
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
        Section Access & Permissions
        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
          ({selectedSections.length} selected)
        </span>
      </label>

      <div className="space-y-2 max-h-[500px] overflow-y-auto p-1">
        {(visibleSections
          ? AVAILABLE_SECTIONS.filter(s => visibleSections.includes(s.id))
          : AVAILABLE_SECTIONS
        ).map((section) => {
          const isSelected = selectedSections.includes(section.id)
          const isExpanded = expandedSection === section.id && isSelected
          const hasGranularOptions = section.granularOptions && section.granularOptions.length > 0

          return (
            <div
              key={section.id}
              className={`rounded-lg border transition-all ${
                isSelected
                  ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 opacity-60'
              }`}
            >
              {/* Section header row */}
              <div className="flex items-center gap-3 p-3">
                {/* Checkbox */}
                <div className="flex-shrink-0 relative flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSection(section.id)}
                    className={`w-4 h-4 rounded border-2 transition-all duration-200 ease-in-out appearance-none cursor-pointer focus:ring-2 focus:ring-offset-0 ${
                      isSelected
                        ? 'bg-gray-900 dark:bg-gray-100 border-gray-900 dark:border-gray-100'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-gray-400'
                    }`}
                  />
                  {isSelected && (
                    <svg className="absolute w-3 h-3 pointer-events-none" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        className="fill-white dark:fill-gray-900"
                      />
                    </svg>
                  )}
                </div>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm transition-colors ${
                    isSelected ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500'
                  }`}>
                    {section.label}
                  </div>
                  <div className={`text-xs mt-0.5 transition-colors ${
                    isSelected ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'
                  }`}>
                    {section.description}
                  </div>
                </div>

                {/* Expand button for granular controls */}
                {isSelected && hasGranularOptions && (
                  <button
                    type="button"
                    onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                    className="flex-shrink-0 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400"
                    title="Configure granular permissions"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}

                {/* Quick access level pill */}
                {isSelected && hasGranularOptions && (
                  <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                    getAccessLevel(section.id) === 'edit'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  }`}>
                    {getAccessLevel(section.id) === 'edit' ? 'Edit' : 'View'}
                  </span>
                )}
              </div>

              {/* Granular controls (expanded) */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700 space-y-4">
                  {/* Access Level */}
                  {section.granularOptions?.some(o => o.type === 'accessLevel') && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                        Access Level
                      </label>
                      <div className="flex gap-2">
                        {(['view', 'edit'] as AccessLevel[]).map((level) => {
                          const isActive = getAccessLevel(section.id) === level
                          const isDisabled = role === 'support' && level === 'edit'
                          return (
                            <button
                              key={level}
                              type="button"
                              disabled={isDisabled}
                              onClick={() => updateSectionField(section.id, 'accessLevel', level)}
                              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all ${
                                isActive
                                  ? level === 'edit'
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-700'
                                    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-700'
                                  : isDisabled
                                    ? 'bg-gray-50 dark:bg-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                    : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {level === 'view' ? 'View Only' : 'Full Edit'}
                            </button>
                          )
                        })}
                      </div>
                      {role === 'support' && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Support members are always view-only
                        </p>
                      )}
                    </div>
                  )}

                  {/* Action Permissions (Shows only) */}
                  {section.granularOptions?.some(o => o.type === 'actionPermissions') && (
                    <ActionPermissionsConfig
                      canAddShows={(sectionConfig as any)[section.id]?.canAddShows ?? true}
                      canEditShowInfo={(sectionConfig as any)[section.id]?.canEditShowInfo ?? true}
                      onUpdate={(field, value) => updateSectionField(section.id, field, value)}
                    />
                  )}

                  {/* Financial Access */}
                  {section.granularOptions?.some(o => o.type === 'financialAccess') && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Financial Data
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {section.id === 'shows'
                            ? 'Guarantees, revenue, payout info'
                            : section.id === 'music'
                            ? 'Royalties, stream revenue'
                            : 'Financial metrics and reports'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateSectionField(section.id, 'financialAccess', !getFinancialAccess(section.id))}
                        disabled={role === 'support'}
                        className={`w-10 h-5.5 rounded-full transition-all flex-shrink-0 ${
                          getFinancialAccess(section.id)
                            ? 'bg-green-500'
                            : 'bg-gray-300 dark:bg-gray-600'
                        } ${role === 'support' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div
                          className={`w-4.5 h-4.5 rounded-full bg-white shadow transition-transform ${
                            getFinancialAccess(section.id) ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                          style={{ width: 18, height: 18, marginTop: 1 }}
                        />
                      </button>
                    </div>
                  )}

                  {/* Ticket Access (Shows only) */}
                  {section.id === 'shows' && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Ticket Data & Updates
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          View and update ticket counts, sold %, capacity
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          updateSectionField(
                            'shows',
                            'ticketAccess',
                            !((sectionConfig as any).shows?.ticketAccess ?? true)
                          )
                        }
                        className={`w-10 rounded-full transition-all flex-shrink-0 ${
                          (sectionConfig as any).shows?.ticketAccess !== false
                            ? 'bg-green-500'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        style={{ height: 20 }}
                      >
                        <div
                          className={`rounded-full bg-white shadow transition-transform ${
                            (sectionConfig as any).shows?.ticketAccess !== false
                              ? 'translate-x-5'
                              : 'translate-x-0.5'
                          }`}
                          style={{ width: 18, height: 18, marginTop: 1 }}
                        />
                      </button>
                    </div>
                  )}

                  {/* Region Filter (Shows only) */}
                  {section.granularOptions?.some(o => o.type === 'regionFilter') && (
                    <RegionFilterConfig
                      regionFilter={getRegionFilter(section.id)}
                      onUpdate={(rf) => updateSectionField(section.id, 'regionFilter', rf)}
                    />
                  )}

                  {/* Date Filter */}
                  {section.granularOptions?.some(o => o.type === 'dateFilter') && (
                    <DateFilterConfig
                      sectionId={section.id}
                      enabled={(sectionConfig as any)[section.id]?.dateFilter?.enabled ?? false}
                      from={(sectionConfig as any)[section.id]?.dateFilter?.from ?? ''}
                      to={(sectionConfig as any)[section.id]?.dateFilter?.to ?? ''}
                      onToggle={(enabled) => updateNestedField(section.id, 'dateFilter', 'enabled', enabled)}
                      onChangeFrom={(v) => updateNestedField(section.id, 'dateFilter', 'from', v)}
                      onChangeTo={(v) => updateNestedField(section.id, 'dateFilter', 'to', v)}
                    />
                  )}

                  {/* Per-Show Filter (Shows only) */}
                  {section.granularOptions?.some(o => o.type === 'showFilter') && (
                    <ShowFilterConfig
                      showFilter={getShowFilter(section.id)}
                      onUpdate={(sf) => updateSectionField(section.id, 'showFilter', sf)}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {selectedSections.length === 0 && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-2">
          Please select at least one section
        </p>
      )}
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────

function RegionFilterConfig({
  regionFilter,
  onUpdate,
}: {
  regionFilter: RegionFilter
  onUpdate: (rf: RegionFilter) => void
}) {
  const [cityInput, setCityInput] = useState('')

  const addItem = (key: 'states' | 'cities' | 'countries', value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return
    const current = regionFilter[key] || []
    if (current.includes(trimmed)) return
    onUpdate({ ...regionFilter, [key]: [...current, trimmed] })
  }

  const removeItem = (key: 'states' | 'cities' | 'countries', value: string) => {
    onUpdate({
      ...regionFilter,
      [key]: (regionFilter[key] || []).filter((v) => v !== value),
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Region Filter
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Restrict to specific locations
          </div>
        </div>
        <button
          type="button"
          onClick={() => onUpdate({ ...regionFilter, enabled: !regionFilter.enabled })}
          className={`w-10 rounded-full transition-all flex-shrink-0 ${
            regionFilter.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          style={{ height: 20 }}
        >
          <div
            className={`rounded-full bg-white shadow transition-transform ${
              regionFilter.enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
            style={{ width: 18, height: 18, marginTop: 1 }}
          />
        </button>
      </div>

      {regionFilter.enabled && (
        <div className="space-y-3 pl-1 mt-3">
          {/* Countries */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Countries</label>
            <div className="flex gap-1 flex-wrap mb-1">
              {(regionFilter.countries || []).map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-xs rounded-full"
                >
                  {c}
                  <button type="button" onClick={() => removeItem('countries', c)} className="hover:text-red-600">
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) addItem('countries', e.target.value)
              }}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="">Add country...</option>
              {COUNTRIES.filter((c) => !(regionFilter.countries || []).includes(c)).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* States/Provinces */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">States / Provinces</label>
            <div className="flex gap-1 flex-wrap mb-1">
              {(regionFilter.states || []).map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs rounded-full"
                >
                  {s}
                  <button type="button" onClick={() => removeItem('states', s)} className="hover:text-red-600">
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) addItem('states', e.target.value)
              }}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="">Add state/province...</option>
              {ALL_REGIONS.filter((s) => !(regionFilter.states || []).includes(s)).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Cities */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cities</label>
            <div className="flex gap-1 flex-wrap mb-1">
              {(regionFilter.cities || []).map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-xs rounded-full"
                >
                  {c}
                  <button type="button" onClick={() => removeItem('cities', c)} className="hover:text-red-600">
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-1">
              <input
                type="text"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addItem('cities', cityInput)
                    setCityInput('')
                  }
                }}
                placeholder="Type city name and press Enter"
                className="flex-1 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              />
              <button
                type="button"
                onClick={() => {
                  addItem('cities', cityInput)
                  setCityInput('')
                }}
                className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
              >
                Add
              </button>
            </div>
          </div>

          {(regionFilter.countries?.length || 0) === 0 &&
            (regionFilter.states?.length || 0) === 0 &&
            (regionFilter.cities?.length || 0) === 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                No regions specified — member will see all shows. Add at least one location to filter.
              </p>
            )}
        </div>
      )}
    </div>
  )
}

function ActionPermissionsConfig({
  canAddShows,
  canEditShowInfo,
  onUpdate,
}: {
  canAddShows: boolean
  canEditShowInfo: boolean
  onUpdate: (field: string, value: boolean) => void
}) {
  const toggles: { key: string; label: string; description: string; value: boolean }[] = [
    { key: 'canAddShows', label: 'Add Shows', description: 'Create new shows (manual, CSV, Bandsintown)', value: canAddShows },
    { key: 'canEditShowInfo', label: 'Edit Show Info', description: 'Edit show details, status, and delete shows', value: canEditShowInfo },
  ]

  return (
    <div>
      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
        Action Permissions
      </div>
      <div className="space-y-2.5">
        {toggles.map((t) => (
          <div key={t.key} className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t.label}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {t.description}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onUpdate(t.key, !t.value)}
              className={`w-10 rounded-full transition-all flex-shrink-0 ${
                t.value ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
              style={{ height: 20 }}
            >
              <div
                className={`rounded-full bg-white shadow transition-transform ${
                  t.value ? 'translate-x-5' : 'translate-x-0.5'
                }`}
                style={{ width: 18, height: 18, marginTop: 1 }}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function DateFilterConfig({
  enabled,
  from,
  to,
  onToggle,
  onChangeFrom,
  onChangeTo,
}: {
  sectionId?: string
  enabled: boolean
  from: string
  to: string
  onToggle: (v: boolean) => void
  onChangeFrom: (v: string) => void
  onChangeTo: (v: string) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Date Filter
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Restrict to a date range
          </div>
        </div>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          className={`w-10 rounded-full transition-all flex-shrink-0 ${
            enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          style={{ height: 20 }}
        >
          <div
            className={`rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
            style={{ width: 18, height: 18, marginTop: 1 }}
          />
        </button>
      </div>

      {enabled && (
        <div className="flex gap-2 pl-1 mt-2">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => onChangeFrom(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => onChangeTo(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            />
          </div>
        </div>
      )}
    </div>
  )
}

type BookingSummary = {
  _id: string
  venue: string
  city: string
  state?: string
  perf_date: string
  event_title?: string
}

function ShowFilterConfig({
  showFilter,
  onUpdate,
}: {
  showFilter: ShowFilter
  onUpdate: (sf: ShowFilter) => void
}) {
  const [bookings, setBookings] = useState<BookingSummary[]>([])
  const [loadingBookings, setLoadingBookings] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [hasFetched, setHasFetched] = useState(false)

  // Fetch bookings when enabled for the first time
  useEffect(() => {
    if (!showFilter.enabled || hasFetched) return
    setLoadingBookings(true)
    getDataAPI('user/getBookings')
      .then((res) => {
        // Match the nested response structure: payload.data.data || payload.data || payload
        const raw = res?.data?.data || res?.data || res || []
        const data = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
        const list: BookingSummary[] = data.map((b: any) => ({
          _id: b._id,
          venue: b.venue || '',
          city: b.city || '',
          state: b.state || '',
          perf_date: b.perf_date || '',
          event_title: b.event_title || '',
        }))
        // Sort by date descending (most recent first)
        list.sort((a, b) => {
          const da = new Date(a.perf_date).getTime() || 0
          const db = new Date(b.perf_date).getTime() || 0
          return db - da
        })
        setBookings(list)
        setHasFetched(true)
      })
      .catch((err) => {
        console.error('Failed to fetch bookings for show filter:', err)
      })
      .finally(() => setLoadingBookings(false))
  }, [showFilter.enabled, hasFetched])

  const toggleShow = (id: string) => {
    const current = showFilter.showIds || []
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    onUpdate({ ...showFilter, showIds: next })
  }

  const removeShow = (id: string) => {
    onUpdate({ ...showFilter, showIds: (showFilter.showIds || []).filter((x) => x !== id) })
  }

  const selectedCount = (showFilter.showIds || []).length

  const formatDate = (raw: string): string => {
    if (!raw) return ''
    try {
      const d = new Date(raw)
      if (isNaN(d.getTime())) return raw
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch {
      return raw
    }
  }

  const filteredBookings = bookings.filter((b) => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (
      (b.venue || '').toLowerCase().includes(q) ||
      (b.city || '').toLowerCase().includes(q) ||
      (b.state || '').toLowerCase().includes(q) ||
      (b.event_title || '').toLowerCase().includes(q) ||
      (b.perf_date || '').toLowerCase().includes(q)
    )
  })

  // Build a lookup for selected show labels (venue + city)
  const selectedShowLabels = (showFilter.showIds || []).map((id) => {
    const b = bookings.find((bk) => bk._id === id)
    if (b) return { id, label: `${b.venue || 'Unknown venue'}${b.city ? `, ${b.city}` : ''} - ${formatDate(b.perf_date)}` }
    return { id, label: id }
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Per-Show Filter
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Restrict access to specific shows
          </div>
        </div>
        <button
          type="button"
          onClick={() => onUpdate({ ...showFilter, enabled: !showFilter.enabled })}
          className={`w-10 rounded-full transition-all flex-shrink-0 ${
            showFilter.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          style={{ height: 20 }}
        >
          <div
            className={`rounded-full bg-white shadow transition-transform ${
              showFilter.enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
            style={{ width: 18, height: 18, marginTop: 1 }}
          />
        </button>
      </div>

      {showFilter.enabled && (
        <div className="space-y-3 pl-1 mt-3">
          {/* Mode selector */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">Mode</label>
            <div className="flex gap-2">
              {(['allowlist', 'blocklist'] as const).map((mode) => {
                const isActive = showFilter.mode === mode
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => onUpdate({ ...showFilter, mode })}
                    className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all ${
                      isActive
                        ? mode === 'allowlist'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-700'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 ring-1 ring-red-300 dark:ring-red-700'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {mode === 'allowlist' ? 'Only these shows' : 'All shows except'}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Selected shows as pills */}
          {selectedCount > 0 && (
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                {showFilter.mode === 'allowlist' ? 'Visible shows' : 'Hidden shows'} ({selectedCount})
              </label>
              <div className="flex gap-1 flex-wrap">
                {selectedShowLabels.map(({ id, label }) => (
                  <span
                    key={id}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${
                      showFilter.mode === 'allowlist'
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    }`}
                  >
                    <span className="max-w-[200px] truncate">{label}</span>
                    <button type="button" onClick={() => removeShow(id)} className="hover:text-red-600 flex-shrink-0">
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Search + show list */}
          <div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search shows by venue, city, date..."
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 mb-2"
            />

            {loadingBookings ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 py-2">Loading shows...</p>
            ) : bookings.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 py-2">No shows found for this artist.</p>
            ) : (
              <div className="max-h-[200px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
                {filteredBookings.map((b) => {
                  const isSelected = (showFilter.showIds || []).includes(b._id)
                  return (
                    <label
                      key={b._id}
                      className={`flex items-center gap-2 px-2.5 py-2 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                        isSelected
                          ? 'bg-gray-50 dark:bg-gray-700/50'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <div className="flex-shrink-0 relative flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleShow(b._id)}
                          className={`w-3.5 h-3.5 rounded border-2 transition-all appearance-none cursor-pointer ${
                            isSelected
                              ? 'bg-gray-900 dark:bg-gray-100 border-gray-900 dark:border-gray-100'
                              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        {isSelected && (
                          <svg className="absolute w-2.5 h-2.5 pointer-events-none" viewBox="0 0 20 20" fill="none">
                            <path
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              className="fill-white dark:fill-gray-900"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                          {b.venue || b.event_title || 'Untitled show'}
                        </div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                          {[b.city, b.state].filter(Boolean).join(', ')}
                          {b.perf_date ? ` \u2022 ${formatDate(b.perf_date)}` : ''}
                        </div>
                      </div>
                    </label>
                  )
                })}
                {filteredBookings.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 p-2 text-center">
                    No shows match your search
                  </p>
                )}
              </div>
            )}
          </div>

          {showFilter.enabled && selectedCount === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {showFilter.mode === 'allowlist'
                ? 'No shows selected — member will not see any shows. Select at least one.'
                : 'No shows selected — member will see all shows. Select shows to hide.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
